export type PrintingStatus = 'FAIL' | 'SUCCESS';

export interface PrintingHistoryEntry {
  id: number;
  userId: number | null;
  printedPieces: number[];
  plotterConfig: {
    unit: number;
    switchAxis: boolean;
    mirror: boolean;
  };
  printedArea: number;
  status: PrintingStatus;
  failError: { message: string; tracelog?: string } | null;
  plotter: { id: number; name: string };
  daemonId: string;
  deviceId: string;
  createdAt: string;
  printableData: {
    id: number;
    modelId: number;
    model: {
      id: number;
      name: string;
      serie: {
        id: number;
        name: string;
        brand: { id: number; name: string };
      };
    };
  };
}

export type printPiecesQuery = {
  fabricState: string;
  plotterConfig: { unit: number; switchAxis: boolean; mirror: boolean };
  printableDataId: number;
};
export type PrintingQuery = {
  ids?: number[];
  modelIds?: number[];
  userIds?: number[];
  adminIds?: number[];
  startDate?: string;
  endDate?: string;
  offset?: number;
  limit?: number;
  exclude?: number[];
};

export type PrintingList = {
  listing: PrintingHistoryEntry[];
  total: number;
};

export type PlotterDetails = PlotterBrand;

export type PrinterConnectionStatus = {
  isUp: boolean;
  devices: PlotterDetails[];
  daemons?: PiDaemon[];
};

export type PiDaemon = {
  daemonId: string;
  addresses: string[];
};

export type PlotterBrand = {
  id: number;
  name: string;
  target: PlotterTarget;
  busy: boolean;
};

export type PlotterTarget = {
  daemonId: string;
  deviceId: string;
  deviceObservationId: string;
};

export function plotterTargetKey(plotter: PlotterTarget): string {
  return `${plotter.daemonId}:${plotter.deviceId}:${plotter.deviceObservationId}`;
}

export function findSelectedPlotter(plotters: readonly PlotterDetails[], selection: string | null | undefined): PlotterDetails | undefined {
  if (!selection) {
    return undefined;
  }

  return plotters.find(plotter => !plotter.busy && plotterTargetKey(plotter.target) === selection);
}

export function reconcilePrinterSelection(plotters: readonly PlotterDetails[], selection: string | null | undefined): string | null | undefined {
  if (findSelectedPlotter(plotters, selection)) {
    return selection;
  }

  // A persisted observation-bound selection must never silently move to a new
  // observation. Keep it stale until the user explicitly selects a current
  // candidate; only a genuinely empty configuration may use the sole-device
  // convenience default.
  if (selection) {
    return selection;
  }

  if (plotters.length !== 1 || plotters[0].busy) {
    return selection;
  }

  return plotterTargetKey(plotters[0].target);
}

export type PlotterBrandList = {
  listing: PlotterBrand[];
  total: number;
};
